/**
 * @license
 * Copyright 2025 Steven Roussey <sroussey@gmail.com>
 * SPDX-License-Identifier: Apache-2.0
 */

import { createServiceToken } from "@workglow/util";
import { JsonSchema } from "@workglow/util/schema";
import { mkdir, readFile, rm, unlink, writeFile } from "fs/promises";
import path from "path";
import { IKvStorage } from "./IKvStorage";
import { KvStorage } from "./KvStorage";

export const FS_FOLDER_KV_REPOSITORY = createServiceToken<IKvStorage<string, any, any>>(
  "storage.kvRepository.fsFolder"
);

/**
 * Key-value repository that stores each value as a file in a folder, using
 * a caller-supplied `pathWriter` to map keys to relative paths.
 */
export class FsFolderKvStorage<
  Key extends string = string,
  Value = any,
  Combined = { key: Key; value: Value },
> extends KvStorage<Key, Value, Combined> {
  constructor(
    public folderPath: string,
    public pathWriter: (key: Key) => string,
    keySchema: JsonSchema = { type: "string" },
    valueSchema: JsonSchema = { contentEncoding: "blob" }
  ) {
    super(keySchema, valueSchema);
  }

  private async setupDirectory(): Promise<void> {
    try {
      await mkdir(this.folderPath, { recursive: true });
    } catch (error) {
      // CI system sometimes has issues temporarily; retry once.
      await new Promise((resolve) => setTimeout(resolve, 0));
      try {
        await mkdir(this.folderPath, { recursive: true });
      } catch {
        // Ignore: directory likely already exists.
      }
    }
  }

  public async put(key: Key, value: Value): Promise<void> {
    const localPath = path.join(this.folderPath, this.pathWriter(key).replaceAll("..", "_"));

    let content: string;
    const schemaType =
      typeof this.valueSchema === "object" &&
      this.valueSchema !== null &&
      "type" in this.valueSchema
        ? this.valueSchema.type
        : undefined;
    if (value === null) {
      content = "";
    } else if (schemaType === "object") {
      content = JSON.stringify(value);
    } else if (typeof value === "object") {
      content = JSON.stringify(value);
    } else {
      content = String(value);
    }

    await mkdir(path.dirname(localPath), { recursive: true });
    await writeFile(localPath, content);
    this.events.emit("put", key, value);
  }

  public async putBulk(items: Array<{ key: Key; value: Value }>): Promise<void> {
    await this.setupDirectory();
    await Promise.all(items.map(async ({ key, value }) => this.put(key, value)));
  }

  /** Parallel single-file reads; missing keys are dropped from the result. */
  public async getBulk(keys: readonly Key[]): Promise<Combined[]> {
    if (keys.length === 0) return [];
    const settled = await Promise.all(
      keys.map(async (key) => {
        const value = await this.get(key);
        if (value === undefined) return undefined;
        return { key, value } as Combined;
      })
    );
    const combined = settled.filter((r) => r !== undefined) as Combined[];
    this.events.emit("getBulk", keys, combined);
    return combined;
  }

  public async get(key: Key): Promise<Value | undefined> {
    const localPath = path.join(this.folderPath, this.pathWriter(key).replaceAll("..", "_"));
    const typeDef = this.valueSchema;
    try {
      const encoding =
        typeof typeDef === "object" &&
        typeDef !== null &&
        "contentEncoding" in typeDef &&
        typeDef.contentEncoding === "blob"
          ? "binary"
          : "utf-8";
      const content = (await readFile(localPath, { encoding })).toString().trim();

      let value: Value;
      if (encoding === "utf-8") {
        const schemaType =
          typeof typeDef === "object" && typeDef !== null && "type" in typeDef
            ? typeDef.type
            : undefined;
        if (
          schemaType === "object" ||
          (content.startsWith("{") && content.endsWith("}")) ||
          (content.startsWith("[") && content.endsWith("]"))
        ) {
          try {
            value = JSON.parse(content) as Value;
          } catch (e) {
            value = content as unknown as Value;
          }
        } else {
          value = content as unknown as Value;
        }
      } else {
        value = content as unknown as Value;
      }

      this.events.emit("get", key, value);
      return value;
    } catch (error) {
      this.events.emit("get", key, undefined);
      return undefined;
    }
  }

  public async delete(key: Key): Promise<void> {
    const localPath = path.join(this.folderPath, this.pathWriter(key).replaceAll("..", "_"));
    await unlink(localPath);
    this.events.emit("delete", key);
  }

  public async getAll(): Promise<Combined[] | undefined> {
    throw new Error("Not implemented");
  }

  public async deleteAll(): Promise<void> {
    const localPath = path.join(this.folderPath);
    await rm(localPath, { recursive: true, force: true });
    this.events.emit("deleteall");
  }

  public async size(): Promise<number> {
    throw new Error("Not implemented");
  }
}
